// ---------- Tool ----------
export type AiTool = {
    /** Map to Azure as: { type:"function", function:{ name, description, parameters, strict? } } */
    type: 'function';
    name: string;                 // snake_case, stable
    description: string;          // short, imperative
    parameters: AiObjectSchema;   // top-level must be an object schema
    strict?: boolean;             // ask Azure to enforce schema exactly
    required?: string[];          // convenience; same as parameters.required
};

// ---------- Schema base ----------
export type JsonPrimitiveType = "string" | "number" | "integer" | "boolean";
export type JsonType = JsonPrimitiveType | "object" | "array";

export interface AiSchemaBase<T> {
    description?: string;
    default?: T;   // must match the schema’s type
}

// ---------- Primitives ----------
export interface AiStringSchema extends AiSchemaBase<string> {
    type: "string";
    enum?: string[];
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    format?:
        | "date-time" | "date" | "time"
        | "uri" | "email" | "uuid"
        | "hostname" | "ipv4" | "ipv6";
}

export interface AiNumberSchema extends AiSchemaBase<number> {
    type: "number" | "integer";
    enum?: number[];
    minimum?: number;
    maximum?: number;
    exclusiveMinimum?: number;
    exclusiveMaximum?: number;
    multipleOf?: number;
}

export interface AiBooleanSchema extends AiSchemaBase<boolean> {
    type: "boolean";
}

// ---------- Object ----------
export interface AiObjectSchema extends AiSchemaBase<Record<string, unknown>> {
    type: "object";
    properties: Record<string, AiToolArgsSchema>;
    required: string[];                 // required lives only on objects
    additionalProperties: boolean;      // keep boolean for Azure strict mode
}

// ---------- Array ----------
export interface AiArraySchema extends AiSchemaBase<unknown[]> {
    type: "array";
    items: AiToolArgsSchema;                      // recommend required for clarity
    minItems?: number;
    maxItems?: number;
    uniqueItems?: boolean;
}

// ---------- Union ----------
export type AiToolArgsSchema =
    | AiStringSchema
    | AiNumberSchema
    | AiBooleanSchema
    | AiObjectSchema
    | AiArraySchema;
